import { localizedSuffix } from "../../../i18n/localizedService";
import { findStageType, getStageTypeLabel } from "./stageTypes";

/**
 * Подписи столбцов (этапов программы) в плане лечения.
 *
 * В шапке столбца живут ДВЕ НЕЗАВИСИМЫЕ подписи (не подменяют друг друга):
 * 1. режим подписи группы `column_type` (0/1 — «Приём N», 2 — дни недели
 *    подряд, 3 — чётные, 4 — нечётные) — хранится на уровне ГРУППЫ;
 *    показывается ОСНОВНОЙ (крупной) строкой сверху;
 * 2. тип приёма (этап программы) — хранится на уровне ПЛАНА по номеру приёма
 *    (`result.appointment_types`), подпись из фиксированного списка этапов
 *    (`stageTypes`) на языке интерфейса; показывается БЛЕДНОЙ строкой снизу
 *    под номером/днём недели.
 *
 * Если тип приёма не задан, второй строки нет (не дублируем подпись).
 */

const text = (value) => {
    if (value == null) return "";
    const raw = typeof value === "object" && "value" in value ? value.value : value;
    return raw == null ? "" : String(raw).trim();
};

/** Карта «номер приёма → название этапа» с учётом языка интерфейса. */
export const buildAppointmentTypeMap = (appointmentTypes, language) => {
    const map = {};
    const suffix = localizedSuffix(language);

    (appointmentTypes || []).forEach((type) => {
        const number = parseInt(text(type?.appointment_number), 10);
        if (!Number.isFinite(number) || number <= 0) return;

        const name = text(type?.name);
        const nameEn = text(type?.name_en ?? type?.nameEn);

        // Тип из фиксированного списка этапов: подпись всегда берём из списка
        // на языке интерфейса — так работает и арабский, для которого
        // отдельной колонки в БД нет.
        const fixed = findStageType(name, nameEn);
        if (fixed) {
            map[number] = getStageTypeLabel(fixed, language);
            return;
        }

        // Планы, где тип вводился вручную до появления фиксированного списка,
        // показываем как есть.
        const primary = text(type?.[`name${suffix}`]);
        const secondary = suffix ? name : nameEn;
        const label = primary || secondary;
        if (label) map[number] = label;
    });

    return map;
};

/**
 * Подпись столбца по режиму группы (`column_type`): день недели или «Приём N».
 * Это самостоятельный инструмент нумерации столбцов — не зависит от того,
 * задан ли тип приёма.
 */
export const getColumnLabel = ({
    appointmentNumber,
    columnType = 0,
    weekDaysShort = [],
    t,
} = {}) => {
    const days = weekDaysShort.length ? weekDaysShort : [];
    const index = Math.max(appointmentNumber - 1, 0);

    if (days.length) {
        if (columnType === 2) return days[index % days.length];
        if (columnType === 3) {
            const even = days.filter((_, i) => (i + 1) % 2 === 0);
            return even.length ? even[index % even.length] : days[index % days.length];
        }
        if (columnType === 4) {
            const odd = days.filter((_, i) => (i + 1) % 2 !== 0);
            return odd.length ? odd[index % odd.length] : days[index % days.length];
        }
    }

    return t("plan.appointment", { number: appointmentNumber });
};

/**
 * Две подписи шапки столбца:
 * `primary` — подпись режима группы (номер приёма / день недели), крупно;
 * `secondary` — тип приёма (этап программы), бледно, если он задан.
 * Пустая строка = вторую строку не рисуем.
 */
export const getStageCellLabels = ({
    appointmentNumber,
    typeMap,
    columnType = 0,
    weekDaysShort = [],
    t,
} = {}) => {
    const columnLabel = getColumnLabel({
        appointmentNumber,
        columnType,
        weekDaysShort,
        t,
    });
    const stageLabel = text(typeMap?.[appointmentNumber]);

    if (!stageLabel || stageLabel === columnLabel) {
        return { primary: columnLabel, secondary: "" };
    }
    return { primary: columnLabel, secondary: stageLabel };
};

/** Одна подпись (печать, старые места): тип приёма с откатом на режим группы. */
export const getStageLabel = ({
    appointmentNumber,
    typeMap,
    columnType = 0,
    weekDaysShort = [],
    t,
} = {}) =>
    text(typeMap?.[appointmentNumber]) ||
    getColumnLabel({ appointmentNumber, columnType, weekDaysShort, t });

/** Список дней недели из настроек перевода (`plan.weekdaysShort`). */
export const getWeekDaysShort = (t) =>
    String(t("plan.weekdaysShort") || "")
        .split(",")
        .map((day) => day.trim())
        .filter(Boolean);
